"use strict";

import {Style, Fill, Stroke} from "ol/style";
import Polygon from "ol/geom/Polygon";
import Feature from "ol/Feature";

/**
 * @classdesc
 * Openlayers3-Papierformat-Overlay.
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis.module.GCPlot
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {gc.gis.module.GCPlot} options.module Modulinstanz
 * @param {gc.gis.map.GCMapView} [options.map={@link gc.gis.GCApplication#map}] Karten-Instanz
 */
gc.gis.module.GCPlot.OLPaperMask = function( options )
{
	gc.gis.GCObservable.call( this );

	gc.gis.assert.isInstance( options.module, gc.gis.module.GCPlot );

	this.module                   = options.module;
	this.map                      = options.map || gc.gis.iface.map;
	this.active                   = options.active;
	this.ol3_map                  = this.map.getMap().getImpl();
    this.visible_bounds_threshold = 150;	// Höhe/Breite in px

	this._initMask();
};
gc.gis.module.GCPlot.OLPaperMask.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Initialisiert den Maskenlayer.
 *
 * @protected
 * @method
 */
gc.gis.module.GCPlot.OLPaperMask.prototype._initMask = function()
{
	var self = this;

	// Layer erstellen
	this._layer = new gc.gis.layer.OLTrackingLayer(this.map.getMap(), {     // Wrapper nutzen, und dann mit OL-Instanzen weiterarbeiten
		mapLibOptions: {
			// renderBuffer: 100,
			updateWhileInteracting: true,
			updateWhileAnimating: true
		}
	});

	this._layer.getImpl().setStyle(this._getMaskStyle());
	var source = this._layer.getImpl().getSource();

	this.map.getMap().addTrackingLayer(this._layer);

	if (!this.active)
	{
		this._layer.setVisible(false);
	}

	// Feature erstellen
	this._geometry = new Polygon([[[0,0],[0,0],[0,0],[0,0]]]);
	var feature = new Feature({
		geometry: this._geometry
	});
	source.addFeature(feature);

	// Auf Events hören
	this.ol3_map.on('change:view', function( evt ) {
		self._setupView( self.ol3_map.getView() );
	});
	this._setupView( this.ol3_map.getView() );

	this.ol3_map.on('moveend', function( evt ) {
		self.updateMask();
	});

	this.module.on( gc.gis.module.GCPlotEvent.LAYOUT_CHANGED, this, function( evt ) {
		self.updateMask();
	});
//	this.module.on( gc.gis.module.GCPlotEvent.ORIENTATION_CHANGED, this, function( evt ) {
//		self.updateMask();
//	});
	this.module.on( gc.gis.module.GCPlotEvent.SCALE_CHANGED, this, function( evt ) {
		self.updateMask();
	});
};

/**
 * Liefert den Tracking-Layer zur Papier-Maske.
 *
 * @public
 * @method
 * @return {gc.gis.layer.OLTrackingLayer}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.getTrackingLayer = function()
{
	return this._layer;
};

/**
 * Gibt den Extent des inneren Rings der Maskengeometrie zurück.
 *
 * @public
 * @method
 *
 * @return {gc.gis.map.GCMapExtent}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.getExtent = function()
{
	return this._geometry.getLinearRing(1).getExtent();
};

/**
 * Liefert die Geometrie der Maske.
 *
 * @public
 * @method
 * @return {gc.gis.geom.GCGeometry}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.getGeometry = function()
{
	var ol_polygon = new Polygon(
		[this._geometry.getLinearRing(1).getCoordinates()]
	);

	return new gc.gis.geom.OLPolygon(ol_polygon);
};

/**
 * Registriert ol.Map-Events zum Updaten der Maske.
 *
 * @protected
 * @method
 *
 * @param {ol.View} view <code>ol.View</code> der <code>ol.Map</code>
 */
gc.gis.module.GCPlot.OLPaperMask.prototype._setupView = function( view )
{
	var self = this;
	view.on('change:center', function( evt ) {
		self.updateMask();
	});
};

/**
 * Berechnet den optimalen Maßstab für das aktuelle Zoom-Level.
 *
 * @public
 * @method
 *
 * @return {number}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.getOptimumScale = function()
{
	var layout      = this.module.getLayout();		// Papiergröße in mm: {width: @integer, height: @integer}
//	var orientation = this.module.getOrientation();	// Papierformat -> Breite/Höhe austauschen?
	var map_scale   = this.map.getScale();
    var plot_scales = this.module.getAvailableScales();

	// Papiergröße in mm
	var width  = layout.width;
	var height = layout.height;
//	if( orientation === 'landscape' )
//	{
//		width  = layout.height;
//		height = layout.width;
//	}

	// Verfügbare Maßstäbe durchlaufen
    for (var i = plot_scales.length - 1; i > 0; i--)
	{
        var ratio = (map_scale / plot_scales[i].value) * (width / height);
		if( ratio >= 1 ) {
            return plot_scales[i].value;
		}
	}

	// Fallback
    return plot_scales[plot_scales.length - 1].value;
};

/**
 * Prüft ob, ob die innere Ringgeometrie der Maske vollständig im aktuellen
 * Ausschnitt sichtbar ist.
 *
 * @public
 * @method
 *
 * @return {boolean}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.isWithinBounds = function()
{
	return this.px_top > 0
	    && this.px_left > 0
	    && this.px_bottom-this.px_top > this.visible_bounds_threshold
	    && this.px_right-this.px_left > this.visible_bounds_threshold;
};

/**
 * Aktualisiert die Maskengeometrie.
 *
 * @public
 * @method
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.updateMask = function()
{
	if( this.active )
	{
		var layout      = this.module.getLayout();		// Papiergröße in mm: {width: @integer, height: @integer}
//		var orientation = this.module.getOrientation();	// Papierformat -> Breite/Höhe austauschen?
		var scale_mm    = this.module.getScale()/1000;	// Maßstab auf mm

		// Papiergröße in mm
		var width  = layout.width;
		var height = layout.height;
//		if( orientation === 'landscape' )
//		{
//			width  = layout.height;
//			height = layout.width;
//		}

		// reale Größe in m
		var width_world  = width*scale_mm;
		var height_world = height*scale_mm;

		// Größe in px
		var f_world    = this.map.getScale()/gc.gis.iface.env.dpm;	// Maßstabsumrechnungsfaktor m->px
		var width2_px  = (width_world/f_world)/2;	// Hälfte der Breite des Papiers in px
		var height2_px = (height_world/f_world)/2;	// Hälfte der Höhe des Papiers in px
		var center_px  = this.map.worldToPixel( this.map.getCenter() );	// Viewport-Mittelpunkt in px

		// Innerer Ring
		this.px_top    = center_px[1]-height2_px;
		this.px_bottom = center_px[1]+height2_px;
		this.px_left   = center_px[0]-width2_px;
		this.px_right  = center_px[0]+width2_px;
		var tl = [this.px_left,  this.px_top];
		var tr = [this.px_right, this.px_top];
		var br = [this.px_right, this.px_bottom];
		var bl = [this.px_left,  this.px_bottom];

		var inner_tl = this.map.pixelToWorld( tl );
		var inner_tr = this.map.pixelToWorld( tr );
		var inner_br = this.map.pixelToWorld( br );
		var inner_bl = this.map.pixelToWorld( bl );

		// Äußerer Ring
		var size = this.ol3_map.getSize();				// screen resolution in px

		var outer_tl = this.map.pixelToWorld( [-999,       -999] );
		var outer_tr = this.map.pixelToWorld( [size[0]+999,-999] );
		var outer_br = this.map.pixelToWorld( [size[0]+999,size[1]+999] );
		var outer_bl = this.map.pixelToWorld( [-999,       size[1]+999] );

		var outer = [outer_tl, outer_tr, outer_br, outer_bl, outer_tl];
		var inner = [inner_tl, inner_tr, inner_br, inner_bl, inner_tl];

		// Koordinaten setzen
		this._geometry.setCoordinates( [outer,inner] );

		// this.ol3_map.render();
	}
};

/**
 * Ist die Maske sichtbar (aktive)?
 *
 * @public
 * @method
 *
 * @return {boolean}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.isActive = function()
{
	return this.active;
};

/**
 * (De-)aktiviert dem Maskenlayer.
 *
 * @param {boolean} active
 */
gc.gis.module.GCPlot.OLPaperMask.prototype.setActive = function( active )
{
	this._layer.setVisible( active === true );
	this.active = active === true;
	if( this.active === true )
	{
		this.module.setScale( this.getOptimumScale() );
	}
	this.updateMask();
};

/**
 * Gibt den Style des Maskenlayers zurück.
 *
 * @protected
 * @method
 *
 * @return {ol.StyleFunction}
 */
gc.gis.module.GCPlot.OLPaperMask.prototype._getMaskStyle = function()
{
	// var stroke = new Stroke({
	// 	color:    '#f00',
	// 	width:    2
	// });
	var fill = new Fill({
		color: 'rgba(0,0,0,0.4)',
		opacity: 0.4
	});

	return function( resolution ) {
		return [new Style({
			fill:   fill/*,
			stroke: stroke*/
		})];
	};
};

